import { IMsalContext } from "@azure/msal-react";
import axios, { AxiosResponse, AxiosError } from "axios";
import { getAccessToken } from "config/token";
import { NEXUS_API_BASE_URL, NEXUS_API_BASE_URL_V2 } from "const";
import { IrequestBody } from ".";

export interface DeleteSpaceResponse {
    success: boolean;
    error?: string;
    status?: number;
}

export interface SubscriptionPayload {
    user_email: string;
    space_name: string;
    domain_name: string;
}

export interface SubscriptionResponse {
    success: boolean;
    error?: string;
}

export interface CheckSubscriptionResponse {
    isSubscribed: boolean;
    error?: string;
}

export const checkSubscription = async (msalInstance: IMsalContext, spaceName: string, userEmail: string): Promise<CheckSubscriptionResponse> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL}/nexus/check-subscription/${spaceName}/${userEmail}`;

        const config = {
            headers: { Authorization: bearerToken },
        };

        const response = await axios.get(requestURL, config);
        return { isSubscribed: response.data?.is_subscribed || false };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to check subscription";
            return { isSubscribed: false, error: errorMessage };
        }
        return { isSubscribed: false, error: "An unexpected error occurred" };
    }
};

export const subscribeToSpace = async (msalInstance: IMsalContext, payload: SubscriptionPayload): Promise<SubscriptionResponse> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL}/nexus/subscribe`;

        const config = {
            headers: { Authorization: bearerToken },
        };

        await axios.post(requestURL, payload, config);
        return { success: true };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to subscribe";
            return { success: false, error: errorMessage };
        }
        return { success: false, error: "An unexpected error occurred" };
    }
};

export const unsubscribeFromSpace = async (msalInstance: IMsalContext, payload: SubscriptionPayload): Promise<SubscriptionResponse> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL}/nexus/unsubscribe`;

        const config = {
            headers: { Authorization: bearerToken },
        };

        await axios.post(requestURL, payload, config);
        return { success: true };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to unsubscribe";
            return { success: false, error: errorMessage };
        }
        return { success: false, error: "An unexpected error occurred" };
    }
};

export const deleteSpace = async (msalInstance: IMsalContext, requestBody: IrequestBody): Promise<DeleteSpaceResponse> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL}/datazone/delete-space`; // WIP: change url to v2

        const config = {
            headers: { Authorization: bearerToken },
        };

        const response = await axios.post(requestURL, requestBody, config);
        console.log(response);
        // const { data }: { data: boolean } = response;
        return { success: true };    
    } catch (error) {
        console.error("Error deleting the space:", error);

        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            const responseData = error.response?.data;
            // Handle the new API error payload structure with app_exception and context
            let errorMessage: string;
            if (responseData?.context?.message) {
                // New API format: { app_exception: "...", context: { status_code: 403, message: "..." } }
                const appException = responseData.app_exception;
                
                // Customize message based on app_exception type
                if (appException === "NonGxpSpaceDeletionPrincipleViolation") {
                    errorMessage = "Action restricted: Non-GxP Space containing active files cannot be deleted";
                } else if (appException === "GxpSpaceDeletionPrincipleViolation") {
                    errorMessage = "Action restricted: GxP Space containing active files or files with delete marker cannot be deleted.";
                } else {
                    // Use the original API message for other exceptions
                    errorMessage = responseData.context.message;
                }
            } else {
                // Generic fallback
                errorMessage = error.message || "Failed to delete space";
            }
            
            return { success: false, error: errorMessage, status };
        } else {
            return { success: false, error: "An unexpected error occurred" };
        }
    }
};

export const getReadOnlyDzLink = async (
	msalInstance: IMsalContext,
	asset_name: string,
)=> {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL}/datazone/fetch-read-only-asset`;

        const params = {
			    asset_name: asset_name,
        };

        const config = {
            headers: { Authorization: bearerToken },
			params : params,
        };
        const response = await axios.get(requestURL, config);
		const data = (response.data).toString();
		return {
			success: true,
			data: data,
		};
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch asset id";
			return {
				success: false,
				data: "",
			};
        }
		return {
			success: false,
			data: "",
		};
    }
};
